import { useId } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';
import { Text } from '@backstage/ui';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { OdooIcon } from '../odooIcons/brandIcons';
import {
  DetailsToggle,
  IconBox,
  waadooIcons,
} from '@internal/plugin-waadoo-core';
import { odooIcons } from '../odooIcons/odooIcons';

/**
 * The icons a section may name in `ui:options.icon` — a closed list, so that
 * a template cannot pick a glyph outside the app's vocabulary
 * (components/ui/icons/waadooIcons.ts). `odoo` is Odoo's mark (components/odooIcons/brandIcons.tsx).
 */
export const SECTION_ICONS = {
  project: waadooIcons.project,
  odoo: 'odoo',
  apps: odooIcons.odooApps,
  modules: odooIcons.odooModules,
  capacity: waadooIcons.resources,
  availability: waadooIcons.highAvailability,
  branch: waadooIcons.branch,
  summary: waadooIcons.summary,
  language: waadooIcons.language,
  database: waadooIcons.database,
  mail: waadooIcons.mailpit,
} as const satisfies Record<string, RemixiconComponentType | 'odoo'>;

export type SectionIconName = keyof typeof SECTION_ICONS;

export const SECTION_ICON_NAMES = Object.keys(SECTION_ICONS) as [
  SectionIconName,
  ...SectionIconName[],
];

export type WaadooSectionUiOptions = {
  icon?: SectionIconName;
  /** Markdown, behind « En savoir plus ». */
  details?: string;
};

function SectionIcon(props: { name: SectionIconName | undefined }) {
  // A name outside the list (a typo in the template) draws no icon; the
  // title stays.
  if (
    !props.name ||
    !Object.prototype.hasOwnProperty.call(SECTION_ICONS, props.name)
  ) {
    return null;
  }
  const glyph = SECTION_ICONS[props.name];
  const Glyph = glyph === 'odoo' ? undefined : glyph;
  return (
    <span
      className="waadoo-sf-section__icon"
      data-icon={props.name}
      aria-hidden="true"
    >
      <IconBox size="medium">
        {Glyph ? (
          <Glyph aria-hidden focusable="false" />
        ) : (
          <OdooIcon fontSize="inherit" />
        )}
      </IconBox>
    </span>
  );
}

/**
 * `ui:field: WaadooSection` — the header of a step of the `odoo-project`
 * form: an icon, the property's `title`, its `description` as a one-line
 * subtitle, and, when `ui:options.details` is set, the long explanation
 * behind a discreet « En savoir plus » toggle (`DetailsToggle`, from the
 * core, which the wizard's sub-header shares).
 *
 * Display only: put it on a property of `type: 'null'`. The field never calls
 * `onChange`, so the property stays out of the form data, and therefore out
 * of the task's parameters and of the review table. Laid out like the card
 * headers of the project pages (`.waadoo-sf-section`, styles.css).
 */
export const WaadooSection = (
  props: FieldExtensionComponentProps<null, WaadooSectionUiOptions>,
) => {
  const { schema, uiSchema } = props;
  const options = uiSchema['ui:options'] ?? {};
  const titleId = useId();

  return (
    <section className="waadoo-sf-section" aria-labelledby={titleId}>
      <div className="waadoo-sf-section__head">
        <SectionIcon name={options.icon} />
        <div className="waadoo-sf-section__text">
          <Text as="h2" variant="title-x-small" id={titleId}>
            {schema.title}
          </Text>
          {schema.description && (
            <Text as="p" variant="body-medium" color="secondary">
              {schema.description}
            </Text>
          )}
          {options.details && <DetailsToggle details={options.details} />}
        </div>
      </div>
    </section>
  );
};
